import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ElementSettings } from '../../../models/report-view.models';
import { Chart } from 'angular-highcharts';
import * as Models from '../../../models/models-index';
import { ChartService } from '../../chart/chart.service';
import { map, takeWhile } from 'rxjs/operators';
import { getChartColors } from '../panel-utils';
import { kpiPieChartPlaceholder } from '../../../constants/highcharts-placeholders';
import { of } from 'rxjs';

@Component({
  selector: 'kpi-pie-chart-stacked-dual-metric',
  templateUrl: './kpi-pie-chart-stacked-dual-metric.component.html',
  styleUrls: ['../panel-elements.scss', './kpi-pie-chart-stacked-dual-metric.component.scss']
})
export class KpiPieChartStackedDualMetricComponent implements Models.ElementComponent, OnInit, OnDestroy {
  @Input() settings: ElementSettings;
  @Input() selectedMetric: string;

  @Input() set dataSets(value: Models.DataSet[]) {
    if (!!value) {
      this.processChartData(value[0]);
      this.processMetricsData(value[1]);
      this.isDataSetLoaded$ = of(true);
    } else {
      this.isDataSetLoaded$ = of(false);
    }
  }

  isDataSetLoaded$ = of(false);
  componentActive: boolean = true;
  currentChart: Chart;
  locale: any;

  placeholderChart: Chart;

  firstMetricValue: any;
  firstMetricValuePreviousPeriod: string;
  firstMetricValuePOP: string;
  firstMetricLabel: string;
  firstMetricTitle: string;
  firstMetricValuePOPNum: number;

  secondMetricValue: any;
  secondMetricValuePreviousPeriod: string;
  secondMetricValuePOP: string;
  secondMetricLabel: string;
  secondMetricTitle: string;
  secondMetricValuePOPNum: number;

  constructor(private chartService: ChartService) {}

  ngOnInit() {
    this.initializePlaceholder();
    this.chartService.reflowChart$
      .pipe(
        takeWhile(() => this.componentActive),
        map(() => {
          setTimeout(() => {
            if (this.currentChart?.ref) {
              this.currentChart.ref.reflow();
            }
          }, 200);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  private processChartData(dataSet: Models.DataSet): void {
    let metricIndex = dataSet.columns.findIndex(col => col.name === this.selectedMetric);
    if (metricIndex === -1) {
      const defaultMetricColumn = dataSet.columns.find(col => col.type === 'metric');
      if (defaultMetricColumn) {
        this.selectedMetric = defaultMetricColumn.name;
        metricIndex = dataSet.columns.findIndex(col => col.name === this.selectedMetric);
      } else {
        this.isDataSetLoaded$ = of(false);
        return;
      }
    }

    if (!this.settings.dimensionNames || this.settings.dimensionNames.length === 0) {
      console.error('Dimension names are not set in the settings');
      this.isDataSetLoaded$ = of(false);
      return;
    }

    const dimIndex = dataSet.columns.findIndex(col => this.settings.dimensionNames.includes(col.name));
    if (dimIndex === -1) {
      this.isDataSetLoaded$ = of(false);
      return;
    }

    let seriesData = [];
    let rows = [...dataSet.rows];
    rows.sort((a, b) => (b[metricIndex].value as number) - (a[metricIndex].value as number));

    rows.forEach(row => {
      if (
        !row[dimIndex]?.value ||
        row[dimIndex].label === '' ||
        this.settings.dimensionRowFilters?.some(f => f === row[dimIndex].value)
      ) {
        return;
      }
      if (!row[metricIndex]?.value) {
        return;
      }
      seriesData.push({
        name: row[dimIndex].label ?? row[dimIndex].value,
        displayValue: row[metricIndex].label ?? row[metricIndex].value,
        y: row[metricIndex].value
      });
    });

    this.currentChart = this.generateChart(seriesData);
  }

  private processMetricsData(dataSet: Models.DataSet): void {
    const row = dataSet.rows[0];
    if (!row) {
      this.isDataSetLoaded$ = of(false);
      return;
    }

    const firstMetric = this.settings.metrics[0];
    const firstMetricIndex = dataSet.columns.findIndex(col => col.name === firstMetric.name);
    if (firstMetricIndex === -1) {
      console.error(`Metric ${firstMetric.name} not found in the dataset.`);
      return;
    }
    const firstMetricPreviousPeriodIndex = dataSet.columns.findIndex(col => col.name === firstMetric.previousPeriod);
    const firstMetricIndexPOP = dataSet.columns.findIndex(col => col.name === firstMetric.pop);

    this.firstMetricLabel = row[firstMetricIndex]?.label ?? row[firstMetricIndex]?.value?.toString();
    this.firstMetricValue = row[firstMetricIndex]?.value;
    this.firstMetricValuePreviousPeriod = row[firstMetricPreviousPeriodIndex]?.label;
    this.firstMetricValuePOP = row[firstMetricIndexPOP]?.label;
    this.firstMetricValuePOPNum = row[firstMetricIndexPOP]?.value as number;
    this.firstMetricTitle = firstMetric.title ?? dataSet.columns[firstMetricIndex]?.displayName ?? dataSet.columns[firstMetricIndex]?.name;

    const secondMetric = this.settings.metrics[1];
    if (secondMetric) {
      const secondMetricIndex = dataSet.columns.findIndex(col => col.name === secondMetric.name);

      if (secondMetricIndex === -1) {
        console.error(`Metric ${secondMetric.name} not found in the dataset.`);
        return;
      }
      const secondMetricPreviousPeriodIndex = dataSet.columns.findIndex(col => col.name === secondMetric.previousPeriod);
      const secondMetricIndexPOP = dataSet.columns.findIndex(col => col.name === secondMetric.pop);

      this.secondMetricLabel = row[secondMetricIndex]?.label ?? row[secondMetricIndex]?.value?.toString();
      this.secondMetricValue = row[secondMetricIndex]?.value;
      this.secondMetricValuePreviousPeriod = row[secondMetricPreviousPeriodIndex]?.label;
      this.secondMetricValuePOP = row[secondMetricIndexPOP]?.label;
      this.secondMetricValuePOPNum = row[secondMetricIndexPOP]?.value as number;
      this.secondMetricTitle = secondMetric.title ?? dataSet.columns[secondMetricIndex]?.displayName ?? dataSet.columns[secondMetricIndex]?.name;
    }
  }

  private generateChart(seriesData: any[]) {
    const colors = getChartColors(this.settings);
    return new Chart({
      credits: {
        enabled: false
      },
      title: { text: ' ' },
      colors,
      chart: {
        type: 'pie',
        height: 400,
        animation: null
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          dataLabels: {
            enabled: true,
            connectorShape: 'straight',
            distance: 30,
            formatter: function () {
              const point = this.point as any;
              return `
                <span style="display:block; font-family: 'Roboto', sans-serif; font-weight:300;">
                  ${point.name}
                </span>
                <span style="display:block; font-family: 'Roboto', sans-serif; font-weight:400;">
                  ${point.displayValue} (${point.percentage.toFixed(0)}%)
                </span>
              `;
            },
            style: {
              fontSize: '14px'
            }
          },
          showInLegend: false,
          states: {
            hover: {
              halo: {
                size: 0
              }
            }
          },
          events: {
            click: () => {}
          }
        },
        series: {
          animation: true,
          point: { events: {} }
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function () {
          const point = this as any;
          return `<b>${point.name}: ${point.displayValue} (${point.percentage.toFixed(0)}%)</b>`;
        }
      },
      legend: {
        enabled: true,
        align: 'center'
      },
      series: [
        {
          type: 'pie',
          data: seriesData
        }
      ]
    });
  }

  private initializePlaceholder(): void {
    if (this.placeholderChart) {
      return;
    }
    setTimeout(() => {
      this.placeholderChart = new Chart(kpiPieChartPlaceholder);
    }, 0);
  }

  getTrendArrowIconClass(popValue: number): string {
    if (popValue === null || popValue === undefined || popValue === 0) {
      return 'fa fa-horizontal-rule';
    } else if (popValue > 0) {
      return 'fa fa-chevron-circle-up chevron-icon';
    } else {
      return 'fa fa-chevron-circle-down chevron-icon';
    }
  }

  getTrendIconColor(popValue: number): string {
    if (popValue === null || popValue === undefined) {
      return 'bg-yellow';
    } else if (popValue > 0) {
      return 'bg-green';
    } else {
      return 'bg-red';
    }
  }
}
